import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { apiClient } from '../../services/api';
import type { TeamInviteInfo } from '../../services/api/teams-api';
import styles from './TeamInviteWelcomeModal.module.css';

interface TeamInviteWelcomeModalProps {
  inviteCode: string;
  onClose: () => void;
  onAccepted: (teamInfo: { teamId: string; teamName: string }) => void;
}

const TeamInviteWelcomeModal = ({ inviteCode, onClose, onAccepted }: TeamInviteWelcomeModalProps) => {
  const { t } = useTranslation('team');
  const { haptic } = useTelegramWebApp();
  const [inviteInfo, setInviteInfo] = useState<TeamInviteInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загрузка информации о приглашении
  useEffect(() => {
    const loadInviteInfo = async () => {
      try {
        const response = await apiClient.teams.getTeamInviteInfo(inviteCode);
        if (response.success && response.invite) {
          setInviteInfo(response.invite);
        } else {
          setError(t('invite.notFound'));
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.response?.data?.error || t('invite.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadInviteInfo();
  }, [inviteCode]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleClose = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic.impact('light');
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  const handleAccept = async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isAccepting || !inviteInfo) return;
    
    setIsAccepting(true);
    haptic.impact('medium');

    try {
      const response = await apiClient.teams.acceptTeamInvite(inviteCode);
      if (response.success && inviteInfo) {
        haptic.notification('success');
        onAccepted({ teamId: inviteInfo.teamId, teamName: inviteInfo.teamName });
      } else {
        setError(t('invite.joinFailed'));
        haptic.notification('error');
      }
    } catch (err: any) {
      const errorCode = err.response?.data?.error;
      // Если пользователь уже участник - это не ошибка, закрываем модалку
      if (errorCode === 'ALREADY_MEMBER' && inviteInfo) {
        haptic.notification('success');
        onAccepted({ teamId: inviteInfo.teamId, teamName: inviteInfo.teamName });
        return;
      }
      setError(err.response?.data?.message || err.response?.data?.error || t('invite.joinFailed'));
      haptic.notification('error');
    } finally {
      setIsAccepting(false);
    }
  };

  const inviterName = inviteInfo?.invitedBy.firstName 
    ? `${inviteInfo.invitedBy.firstName}${inviteInfo.invitedBy.lastName ? ' ' + inviteInfo.invitedBy.lastName : ''}`
    : inviteInfo?.invitedBy.username || t('invite.user');

  const teamInitials = inviteInfo?.teamName?.[0]?.toUpperCase() || 'T';

  return createPortal(
    <div 
      className={styles.backdrop} 
      onClick={handleBackdropClick}
      onTouchEnd={handleBackdropClick}
    >
      <div 
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <button 
          className={styles.closeButton} 
          onClick={handleClose}
          onTouchEnd={handleClose}
          title={t('btn.close', { ns: 'common' })}
        >
          ×
        </button>

        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <span>{t('loading', { ns: 'common' })}</span>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h2 className={styles.errorTitle}>{t('invite.invalid')}</h2>
              <p className={styles.errorText}>{error}</p>
              <button 
                className={styles.closeBtn} 
                onClick={handleClose}
                onTouchEnd={handleClose}
              >
                {t('btn.close', { ns: 'common' })}
              </button>
            </div>
          ) : inviteInfo && (
            <>
              <h2 className={styles.title}>{t('invite.title')}</h2>

              <div className={styles.avatarContainer}>
                {inviteInfo.teamAvatarUrl ? (
                  <img src={inviteInfo.teamAvatarUrl} alt={inviteInfo.teamName} className={styles.avatar} />
                ) : (
                  <div className={styles.avatarPlaceholder}>
                    {teamInitials}
                  </div>
                )}
                <div className={styles.teamBadge}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M17 21V19C17 16.79 15.21 15 13 15H5C2.79 15 1 16.79 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M23 21V19C23 17.14 21.73 15.57 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M16 3.13C17.73 3.57 19 5.14 19 7C19 8.86 17.73 10.43 16 10.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>

              <div className={styles.teamName}>{inviteInfo.teamName}</div>
              
              <div className={styles.membersInfo}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21V19C17 16.79 15.21 15 13 15H5C2.79 15 1 16.79 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
                {inviteInfo.membersCount} {inviteInfo.membersCount === 1 ? t('invite.member') :
                  inviteInfo.membersCount < 5 ? t('invite.members_few') : t('invite.members_many')}
              </div>

              <div className={styles.divider}></div>

              <div className={styles.invitedBySection}>
                <span className={styles.invitedByLabel}>{t('invite.invitedBy')}</span>
                <span className={styles.invitedByName}>{inviterName}</span>
                {inviteInfo.invitedBy.username && (
                  <span className={styles.invitedByUsername}>@{inviteInfo.invitedBy.username}</span>
                )}
              </div>

              <div className={styles.infoBox}>
                <p>{t('invite.afterJoin')}</p>
                <ul>
                  <li>{t('invite.useTeamTokens')}</li>
                  <li>{t('invite.createContent')}</li>
                  <li>{t('invite.seeOthersWork')}</li>
                </ul>
              </div>

              <button 
                className={styles.actionButton} 
                onClick={handleAccept}
                onTouchEnd={handleAccept}
                disabled={isAccepting}
              >
                {isAccepting ? (
                  <div className={styles.buttonSpinner} />
                ) : (
                  t('invite.joinButton')
                )}
              </button>

              <button 
                className={styles.cancelButton} 
                onClick={handleClose}
                onTouchEnd={handleClose}
                disabled={isAccepting}
              >
                {t('btn.notNow', { ns: 'common' })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamInviteWelcomeModal;

